import React, { useState, useEffect } from 'react';
import { BookOpen, ChevronDown, ChevronUp, X, Printer, Library, GraduationCap } from 'lucide-react';
import CHEAT_SHEETS from '../../data/cheatSheets';
import CHEAT_SHEET_EXAMPLES from '../../data/cheatSheetExamples';
import { printSheet, printHandbook } from '../../utils/handbookPrint';

const BRAND = '#F5831F';
const MUTED = '#5B5348';

const WorkedExamples = ({ examples }) => {
  const [selected, setSelected] = useState(0);
  const ex = examples[selected];
  return (
    <div className="px-4 pb-4" data-testid="cheatsheet-examples">
      <div className="flex items-center gap-2 mb-2">
        <GraduationCap size={14} style={{ color: '#2EAA5C' }} />
        <p className="text-xs font-bold uppercase tracking-wider" style={{ color: '#2EAA5C' }}>Worked Examples</p>
        {examples.length > 1 && (
          <select
            value={selected}
            onChange={(e) => setSelected(Number(e.target.value))}
            className="ml-2 text-xs rounded-md px-2 py-1 border"
            style={{ borderColor: '#E8E0D8', color: '#1A1209', backgroundColor: '#FFF' }}
            data-testid="cheatsheet-example-select"
          >
            {examples.map((x, i) => <option key={i} value={i}>{x.label}</option>)}
          </select>
        )}
      </div>
      <div className="p-3 rounded-lg" style={{ backgroundColor: '#F2FAF5', border: '1px solid #D7EDE0' }}>
        <p className="text-sm font-bold mb-1" style={{ color: '#1A1209' }}>{ex.label}</p>
        <p className="text-xs italic mb-2" style={{ color: MUTED }}>{ex.scenario}</p>
        <ol className="space-y-1.5 list-decimal pl-5">
          {ex.steps.map((step, i) => (
            <li key={i} className="text-xs leading-relaxed" style={{ color: '#3A342C' }}>{step}</li>
          ))}
        </ol>
      </div>
    </div>
  );
};

const CheatSheet = ({ pageId }) => {
  const [open, setOpen] = useState(false);
  // Listen for the global "?" shortcut (Phase 4, item 16). Each mounted
  // CheatSheet toggles independently; the shortcut applies to whichever
  // sheet is currently on screen.
  useEffect(() => {
    const handler = () => setOpen((prev) => !prev);
    window.addEventListener('hibff:cheatsheet-toggle', handler);
    return () => window.removeEventListener('hibff:cheatsheet-toggle', handler);
  }, []);
  const sheet = CHEAT_SHEETS[pageId];
  if (!sheet) return null;
  const examples = CHEAT_SHEET_EXAMPLES[pageId] || [];

  return (
    <div className="mb-5" data-testid={`cheatsheet-${pageId}`}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
        style={{ backgroundColor: open ? `${BRAND}12` : '#F5F5F5', color: open ? BRAND : MUTED }}
        data-testid="cheatsheet-toggle"
      >
        <BookOpen size={14} />
        <span>Cheat Sheet</span>
        {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
      </button>

      {open && (
        <div
          className="mt-2 bg-white rounded-lg overflow-hidden animate-in slide-in-from-top-1"
          style={{ border: '1px solid #E8E0D8' }}
          data-testid="cheatsheet-content"
        >
          <div className="flex items-center justify-between px-4 py-2.5 border-b" style={{ borderColor: '#E8E0D8', backgroundColor: '#FDFAF6' }}>
            <div className="flex items-center gap-2">
              <BookOpen size={14} style={{ color: BRAND }} />
              <p className="text-sm font-bold" style={{ color: '#1A1209' }}>{sheet.title}</p>
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => printSheet(pageId)}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold hover:bg-orange-50 transition-colors"
                style={{ color: BRAND, border: `1px solid ${BRAND}40` }}
                title="Print this sheet or save it as a PDF"
                data-testid="cheatsheet-print-btn"
              >
                <Printer size={12} />
                <span>Print / PDF</span>
              </button>
              <button
                onClick={printHandbook}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold hover:bg-purple-50 transition-colors"
                style={{ color: '#7B4FD8', border: '1px solid #7B4FD840' }}
                title="Compile every cheat sheet + worked example into one printable Owner's Handbook"
                data-testid="cheatsheet-handbook-btn"
              >
                <Library size={12} />
                <span>Full Handbook</span>
              </button>
              <button onClick={() => setOpen(false)} className="p-1 rounded-lg hover:bg-gray-100" data-testid="cheatsheet-close-btn">
                <X size={12} style={{ color: MUTED }} />
              </button>
            </div>
          </div>

          <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sheet.sections.map((section, i) => (
              <div key={i} className="p-3 rounded-lg" style={{ backgroundColor: '#FAFAF8' }}>
                <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: BRAND }}>
                  {section.heading}
                </p>
                <ul className="space-y-1.5">
                  {section.bullets.map((bullet, j) => (
                    <li key={j} className="flex items-start gap-2">
                      <span className="w-1 h-1 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: BRAND }} />
                      <span className="text-xs leading-relaxed" style={{ color: '#3A342C' }}>
                        {bullet}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {examples.length > 0 && <WorkedExamples examples={examples} />}
        </div>
      )}
    </div>
  );
};

export default CheatSheet;
